import { useDemo } from '../../api/app-context';
import { IfWrites } from '../write-access';
import { useState } from 'react';
import type { Fact } from '@ni/shared';
import { useAnswerDecision } from '../../api/hooks';
import { standingAnswerLine, standingAnswerOf } from '../../api/errors';
import {
  EVIDENCE,
  FACT_TYPE_LABELS,
  KEPT_JUST_NOW,
  NEGATION_LABELS,
  SEVERITY_WORDS,
} from '../../lib/copy';
import { corroborationClause, factDating, factLine, readsClear } from '@ni/shared';
import { formatDate, joinClauses } from '../../lib/format';
import { useOrg } from '../../api/app-context';
import { sourceHost, sourceTitle, type FactSource } from './fact-sources';
import { CorrectThis } from '../correct-this/correct-this';
import { ErrorLine } from '../ui/error-line';
import { Claims } from './claims';
import { WorkingButton } from '../ui/working-button';
import { Button } from '../ui/button';
import './findings.css';
import './evidence.css';

/**
 * One fact on a research panel (blend spec, step 4): its type in a label
 * column, the statement, and its newest source and date in the margin with no
 * click needed. A `finding` — free prose with no type to label — sits as a line
 * in its source's group instead, and the group carries the margin.
 *
 * **What marks a row is severity and review state, never classification**, and
 * it marks it in words (`severityWord`): no tinted band and no colored rule.
 * The `sensitive` stamp is a quiet audit marker on a row that may be perfectly
 * calm. "Clear" stops at a records check, because "clear" is a statement about
 * a *search* and beside a case caption it would read as a verdict on the case
 * (`readsClear`). A removed row stays listed, greyed, with a plain label.
 */
/**
 * The word a row's severity earns: "Clear" and "Noted" in the calm ink, the
 * level of an adverse one in copper, with "needs review" while the office owes
 * an answer on it. Words, never a score, and no tinted band or colored rule.
 */
export function severityWord(
  fact: Fact,
  { off, queued }: { off: boolean; queued: boolean },
): { word: string; act: boolean } | null {
  if (off) return null;
  const tail = queued ? ` · ${EVIDENCE.needsReview}` : '';
  switch (fact.severity) {
    case 'none':
      return readsClear(fact, { off }) && fact.fact_type === 'records_check'
        ? { word: SEVERITY_WORDS.none + tail, act: queued }
        : queued
          ? { word: EVIDENCE.needsReview, act: true }
          : null;
    case 'info':
      return { word: SEVERITY_WORDS.info + tail, act: queued };
    case 'moderate':
      return { word: SEVERITY_WORDS.moderate + tail, act: true };
    case 'severe':
      return { word: SEVERITY_WORDS.severe + tail, act: true };
    default:
      return queued ? { word: EVIDENCE.needsReview, act: true } : null;
  }
}

export function FactRow({
  fact,
  subjectId,
  subjectName,
  queuedDecisionId,
  runId,
  anchored = true,
  variant = 'row',
  source = null,
  line = null,
  readOnly = false,
}: {
  fact: Fact;
  subjectId: string;
  subjectName: string;
  queuedDecisionId: string | null;
  runId: string | null;
  /**
   * Whether this copy of the row owns the `#fact-<lineage>` anchor. Notable
   * findings re-render rows that also list in their own section, so exactly one
   * copy may carry the id — and it is the section's, which is where a deep link
   * means to land.
   */
  anchored?: boolean;
  /** A labelled row with its own margin, or a line inside its source's group. */
  variant?: 'row' | 'finding';
  /** The newest source behind the fact, once the Data read has landed. */
  source?: FactSource | null;
  /** The sentence, where the reader is not the fact's subject (an organization's panel). */
  line?: string | null;
  /** No Correct and no review answers: a record whose facts are read here, not corrected. */
  readOnly?: boolean;
}) {
  const demo = useDemo();
  const { vertical } = useOrg();
  const [showClaims, setShowClaims] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [settled, setSettled] = useState<string | null>(null);
  const answer = useAnswerDecision(subjectId);

  const off = fact.status !== 'active';
  const queued = !demo && !readOnly && queuedDecisionId !== null && !off;
  const word = severityWord(fact, { off, queued: queued && !settled });

  const offLabel = off
    ? fact.negation_reason
      ? NEGATION_LABELS[fact.negation_reason]
      : 'no longer current'
    : null;

  const dating = factDating(fact);
  const standing = standingAnswerOf(answer.error);
  /* The row is showing its keep / leave out / correct group, so the hover
     Correct stands down for as long as it is up. */
  const answering = queued && !settled;

  const classes = [
    'fact',
    variant === 'row' ? 'fact-row' : 'fact-line',
    off ? 'off' : null,
    answering ? 'queued' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const statement = (
    <>
      {line ?? factLine(fact, { id: subjectId, name: subjectName })}
      {variant === 'row' && dating ? <span className="d"> · {dating}</span> : null}
      {offLabel ? <span className="tag-faint">{offLabel}</span> : null}
      {word ? <span className={word.act ? 'sev act' : 'sev'}>{word.word}</span> : null}
      {fact.sensitivity === 'sensitive' ? <span className="tag-faint">sensitive</span> : null}
    </>
  );

  /* The margin: the newest source and its date, with nothing to press. Pressing
     it opens the stored excerpts, which is the citation one click away. Before
     the Data read lands it says what the record read knows: how many sources,
     and when last confirmed. */
  const margin = (
    <button
      type="button"
      className={source?.office ? 'src-note office' : 'src-note'}
      aria-expanded={showClaims}
      onClick={() => setShowClaims((value) => !value)}
    >
      {source ? (
        <>
          <span className="t">{sourceTitle(vertical, source)}</span>
          <span className="dt">
            {joinClauses([
              source.date ? formatDate(source.date) : sourceHost(source.url),
              corroborationClause(fact),
            ])}
          </span>
        </>
      ) : (
        <span className="dt">
          {joinClauses([
            fact.last_corroborated ? formatDate(fact.last_corroborated) : null,
            corroborationClause(fact),
          ]) || 'Sources'}
        </span>
      )}
    </button>
  );

  const after = (
    <>
      {answering ? (
        <>
          <div className="queued-note">Flagged for review.</div>
          <IfWrites>
            <div className="queued-actions">
              <WorkingButton
                label="Keep"
                workingLabel="Keeping…"
                variant="act"
                tiny
                working={answer.isPending}
                onClick={() =>
                  answer.mutate(
                    { decisionId: queuedDecisionId, body: { keep: true }, runId },
                    { onSuccess: () => setSettled(KEPT_JUST_NOW) },
                  )
                }
              />
              <WorkingButton
                label="Leave out"
                workingLabel="Leaving out…"
                variant="act"
                tiny
                working={answer.isPending}
                onClick={() =>
                  answer.mutate(
                    { decisionId: queuedDecisionId, body: { leave_out: true }, runId },
                    { onSuccess: () => setSettled('Kept off the brief · just now') },
                  )
                }
              />
              <Button variant="act" tiny onClick={() => setCorrecting(true)}>
                Correct
              </Button>
            </div>
          </IfWrites>
          {standing ? <div className="src">{standingAnswerLine(standing)}</div> : null}
          {answer.isError && !standing ? <ErrorLine error={answer.error} /> : null}
        </>
      ) : null}
      {settled ? <div className="src">{settled}</div> : null}
      {showClaims ? <Claims lineageId={fact.lineage_id} /> : null}
    </>
  );

  /* Correct is a rare action on a row that is usually only being read, so it
     waits for the row: hover or focus, always shown on a touch screen. */
  const correct =
    answering || readOnly ? null : (
      <IfWrites>
        <button type="button" className="fix" onClick={() => setCorrecting(true)}>
          Correct
        </button>
      </IfWrites>
    );

  return (
    <li className={classes} id={anchored ? `fact-${fact.lineage_id}` : undefined}>
      {variant === 'row' ? (
        <>
          <span className="k">{FACT_TYPE_LABELS[fact.fact_type]}</span>
          <div className="v">
            {statement}
            {after}
          </div>
          {margin}
        </>
      ) : (
        <div className="v">
          {statement}
          <button
            type="button"
            className="line-src"
            aria-expanded={showClaims}
            onClick={() => setShowClaims((value) => !value)}
          >
            {EVIDENCE.source}
          </button>
          {after}
        </div>
      )}
      {correct}

      {!demo && correcting ? (
        <CorrectThis
          subjectId={subjectId}
          fact={fact}
          queuedDecisionId={queued ? queuedDecisionId : null}
          runId={runId}
          onClose={() => setCorrecting(false)}
        />
      ) : null}
    </li>
  );
}
